import {Component, Inject, OnInit, signal} from '@angular/core';
import {tap} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
  FormControl
} from '@angular/forms';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {
  DatetimePickerComponent,
  DialogCloseButtonComponent,
  InfoMessageComponent,
  LinkButtonComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  TextareaFieldComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {ReleaseSelectComponent} from '@q9elements/ui-kit/changes/shared';
import {addWeeks, endOfDay, isSameDay} from 'date-fns';
import {MapRoles} from '@q9elements/ui-kit/common';
import {BaseReadyForForm} from '../../../utils/base-ready-for-form';
import {AuthService} from '../../../../../core/auth/auth.service';
import {RequestsService} from '../../../../../core/requests/requests.service';
import {DiagramJsonHandlerService} from '../../../../../diagram-data/diagram-json-handler.service';
import {VersionsService} from '../../../../../core/services/versions.service';
import {
  AUTHORISATION_OVERRIDE_REQUEST_ACTION,
  AUTHORISATION_REQUEST_ACTION
} from '../../../models/diagram-version.constant';
import {ERROR_STATUSES} from '../../../../../core/values/error-statuses';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {LowerLevelsContentComponent} from '../lower-levels-content/lower-levels-content.component';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {CommonModule} from '@angular/common';

interface IFormExtendFields {
  name: string;
  control: UntypedFormControl;
}

interface IFormSubmit {
  comment: string;
  password: string;
  children: boolean;
  action: string;
  release?: string;
}

interface IRequestAuthorisationSubmit extends IFormSubmit {
  requiredBy: string;
}

@Component({
  selector: 'app-request-authorisation-popup',
  templateUrl: './request-authorisation-popup.component.html',
  styleUrls: ['./request-authorisation-popup.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSlideToggleModule,
    TranslateModule,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    InfoMessageComponent,
    DatetimePickerComponent,
    TextareaFieldComponent,
    TextFieldComponent,
    ReleaseSelectComponent,
    LinkButtonComponent,
    LowerLevelsContentComponent,
    MatSlideToggleModule
  ]
})
export class RequestAuthorisationPopupComponent extends BaseReadyForForm implements OnInit {
  public defaultDate = addWeeks(new Date(), 1);
  public isManager: boolean;
  public step = signal(1);
  public isLoading = signal(false);

  private extendedFields: IFormExtendFields[] = [
    {name: 'override', control: new UntypedFormControl(false)},
    {name: 'requiredBy', control: new UntypedFormControl(this.defaultDate)}
  ];
  private readyForReleaseMessage = this.translateSvc.instant('VERSIONING.MAKE_READY_SUCCESS');
  private beingAuthorisedMessage = this.translateSvc.instant('VERSIONING.SEND_FOR_AUTHORISATION');

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: any,
    private authService: AuthService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private requestsSvc: RequestsService,
    private dialogRef: MatDialogRef<RequestAuthorisationPopupComponent>,
    private versionsSvc: VersionsService,
    private translateSvc: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.fetchReleases();
    this.initializeForm(this.authService.getUserDataSync().havePassword);
    this.extendForm(this.extendedFields);

    this.overrideFormControl.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        tap(isOverride =>
          isOverride
            ? this.form.controls['requiredBy'].disable()
            : this.form.controls['requiredBy'].enable()
        )
      )
      .subscribe();

    this.isManager = this.authService.getMACR()['role'] === MapRoles.MANAGE;

    if (!this.isManager) {
      this.overrideFormControl.disable({emitEvent: false});
    }
  }

  get taskReadyForReleaseConfirm(): RequestTask {
    const data: IFormSubmit = {
      action: AUTHORISATION_OVERRIDE_REQUEST_ACTION,
      children: this.form.get('children').value,
      comment: this.form.get('comment').value,
      password: ''
    };

    if (this.releaseValue) {
      data.release = this.releaseValue;
    }

    if (this.havePassword) {
      data.password = this.form.get('password').value;
    }

    return {
      isApi: true,
      method: 'POST',
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/state/draft`,
      data
    };
  }

  get taskRequestAuthorisationConfirm(): RequestTask {
    const data: IRequestAuthorisationSubmit = {
      action: AUTHORISATION_REQUEST_ACTION,
      children: this.form.get('children').value,
      comment: this.form.get('comment').value,
      requiredBy: this.form.get('requiredBy').value,
      password: ''
    };

    if (isSameDay(new Date(), new Date(data.requiredBy))) {
      data.requiredBy = endOfDay(new Date()).toISOString();
    }

    if (this.releaseValue) {
      data.release = this.releaseValue;
    }

    if (this.havePassword) {
      data.password = this.form.get('password').value;
    }

    return {
      isApi: true,
      method: 'POST',
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/state/draft`,
      data
    };
  }

  get overrideFormControl(): AbstractControl {
    return this.form.get('override') as FormControl;
  }

  get childrenControl() {
    return this.form.get('children') as FormControl;
  }

  get releaseValue() {
    return this.form.get('release').value;
  }

  public async handleOnSubmit() {
    if (this.form.invalid || this.isLoading()) {
      return;
    }

    const override = this.overrideFormControl.getRawValue();
    const task = override ? this.taskReadyForReleaseConfirm : this.taskRequestAuthorisationConfirm;
    const message = override ? this.readyForReleaseMessage : this.beingAuthorisedMessage;

    try {
      this.isLoading.set(true);
      await this.requestsSvc.addToQueue(task, true);
      await this.versionsSvc.getVersions();
      this.dialogRef.close({success: true, message, value: override});
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private handleError(response): void {
    switch (true) {
      case response.error.status === 'err.authorisation.no_assigned_authorisers':
        this.error = 'POPUPS.NO_AUTHORISERS_WARNING';
        break;

      case response.error.status === ERROR_STATUSES.MAP.ERROR.NO_READY_FOR_AUTHORISATIONS_DIAGRAMS:
        this.dialogRef.close({success: false, needReload: true, message: response.error.error});
        break;

      default:
        this.error = response.error.error;
    }
  }

  private fetchReleases() {
    this.passedData.pipe(this.takeUntilComponentDestroy()).subscribe(data => {
      this.releases = data.releases;
      this.versionRelease = data.draftDiagramRelease;
    });
  }

  private extendForm(extendFields: IFormExtendFields[]): void {
    extendFields.forEach(form => {
      this.form.addControl(form.name, form.control);
    });
  }
}
